import { MessageCircle, Mail, MapPin, Linkedin } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo-footer.webp";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useLang } from "@/i18n/LanguageContext";
import { whatsappLinkProps } from "@/lib/whatsapp";

const Footer = () => {
  const year = new Date().getFullYear();
  const { pathname } = useLocation();
  const { t, localePath } = useLang();

  const handleLogoClick = () => {
    if (pathname === "/" || pathname === "/es" || pathname === "/pt") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.dispatchEvent(new Event("home:reset"));
    }
  };

  return (
    <footer className="bg-foreground pb-24 text-background md:pb-0">
      <div className="container py-6 md:py-9">
        {/* Top: Brand + Newsletter */}
        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:gap-12">
          <div>
            <Link to={localePath("/")} onClick={handleLogoClick} aria-label="Switzh home" className="flex items-center overflow-hidden">
              <img
                src={logo}
                alt="Switzh"
                // Image is pre-cropped tightly with a #1A1A1A background that blends
                // with the footer's bg-foreground color, so no clipPath is needed.
                className="h-12 w-auto sm:h-14 md:h-16"
                width={1374}
                height={338}
                loading="lazy"
              />
            </Link>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-background/70 sm:mt-3">
              {t("footer.tagline")}
            </p>
          </div>
          <div className="lg:max-w-md lg:justify-self-end lg:w-full">
            <NewsletterSignup />
          </div>
        </div>

        {/* Divider */}
        <div className="my-7 border-t border-background/10 sm:my-9" />

        {/* Link columns */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-2 md:grid-cols-4 lg:gap-x-12">
          {/* Solutions — must mirror the header primary nav exactly:
              What We Source → Pre-Configured Power Packages → Power Distribution Solutions → How It Works */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/50">
              {t("footer.solutions")}
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <Link
                  to={localePath("/what-we-source")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.whatWeSource")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/fast-access-power-components")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  Fast Access Power Components
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/pre-configured-power-packages")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.kits")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/power-systems")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.engineeredSolutions")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/how-it-works")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.howItWorks")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Explore */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/50">
              {t("footer.explore")}
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <Link
                  to={localePath("/industries")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.industries")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/case-studies")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.caseStudies")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/compare")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("footer.compare")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/blog")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  Insights
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/about")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/contact")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("footer.contact")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/supplier-partners")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("footer.supplierPartners")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 sm:col-span-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/50">
              {t("footer.contact")}
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a
                  {...whatsappLinkProps("footer")}
                  className="flex items-center gap-2.5 text-background/80 transition-colors hover:text-background"
                >
                  <MessageCircle className="h-4 w-4 shrink-0" />
                  <span className="font-medium text-background">+1 (305) 555 PRIME</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:quotes@switzh.com"
                  className="flex items-center gap-2.5 text-background/80 transition-colors hover:text-background"
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="font-medium text-background text-[13px] leading-none tracking-[-0.01em] sm:text-sm">
                    quotes@switzh.com
                  </span>
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-background/80">
                <MapPin className="h-4 w-4 shrink-0" />
                Miami, FL
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/50">
              {t("footer.legal")}
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <Link
                  to={localePath("/privacy")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/terms")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  {t("footer.terms")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("consent:open"))}
                  className="text-left text-background/80 transition-colors hover:text-background"
                >
                  {t("footer.cookiePrefs")}
                </button>
              </li>
              <li>
                <Link
                  to={localePath("/sitemap")}
                  className="text-background/80 transition-colors hover:text-background"
                >
                  Sitemap
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar — left-aligned stacked on mobile, centered on desktop */}
        <div className="mt-7 flex flex-col items-start justify-center gap-3 border-t border-background/10 pt-5 text-left sm:mt-10 sm:items-center sm:text-center">
          <p className="text-xs text-background/60">
            © {year} Switzh. {t("footer.copy")}
          </p>
          <div className="flex flex-wrap items-center justify-start gap-x-4 gap-y-2 sm:justify-center">
            <a
              href="https://www.linkedin.com/company/switzh"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Switzh on LinkedIn"
              title="Follow us on LinkedIn"
              className="group inline-flex h-8 w-8 items-center justify-center rounded-md bg-background/5 text-background/70 ring-1 ring-background/10 transition-all hover:-translate-y-0.5 hover:bg-[#0A66C2] hover:text-white hover:ring-[#0A66C2] hover:shadow-[0_4px_12px_-2px_rgba(10,102,194,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A66C2] focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
            >
              <Linkedin className="h-4 w-4 transition-transform group-hover:scale-110" fill="currentColor" strokeWidth={0} />
            </a>
            <a
              href="https://x.com/switzh"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Switzh on X"
              title="Follow us on X"
              className="group inline-flex h-8 w-8 items-center justify-center rounded-md bg-background/5 text-background/70 ring-1 ring-background/10 transition-all hover:-translate-y-0.5 hover:bg-black hover:text-white hover:ring-black hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="h-3.5 w-3.5 transition-transform group-hover:scale-110"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <span className="hidden sm:inline text-background/20">|</span>
            <a
              href="https://www.switzh.com/account"
              className="text-xs text-background/60 transition-colors hover:text-background"
            >
              {t("footer.buyerLogin")}
            </a>
            <Link
              to={localePath("/supplier-login")}
              className="text-xs text-background/60 transition-colors hover:text-background"
            >
              {t("footer.supplierLogin")}
            </Link>
            <span className="hidden sm:inline text-background/20">|</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-background/60">
              <span>Language:</span>
              <LanguageSwitcher variant="full" align="end" className="text-background/70 hover:text-background" />
            </span>
            <p className="text-xs text-background/60">
              {t("footer.tagline2")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
